import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ActivityIndicator, FlatList, Pressable, TextInput, View } from 'react-native'
import { ComposeBox } from '@/components/community/ComposeBox'
import { PostCard } from '@/components/community/PostCard'
import { Icon } from '@/components/ui/Icon'
import { Text } from '@/components/ui/Text'
import { useAuth } from '@/contexts/AuthContext'
import { useThemeColors } from '@/contexts/ThemeContext'
import { useAnalytics } from '@/hooks/useAnalytics'
import { useCommunityFeed } from '@/hooks/useCommunityFeed'
import { getMyCohort } from '@/lib/api'

type SortMode = 'new' | 'top'
type FeedAccessState = 'checking' | 'allowed' | 'blocked'

// ─── Hairline Separator ───────────────────────────────────────────────────────
const FrequencyLine = () => {
  const colors = useThemeColors()
  return (
    <View className="flex-row items-center px-6 my-0.5">
      <View className="h-px flex-1 opacity-10" style={{ backgroundColor: colors.muted }} />
      <View
        className="mx-2 w-1 h-1 rounded-full opacity-20"
        style={{ backgroundColor: colors.primary }}
      />
      <View className="h-px w-6 opacity-[0.06]" style={{ backgroundColor: colors.muted }} />
    </View>
  )
}

// ─── Signal Bars (decorative) ─────────────────────────────────────────────────
const _SignalBars = ({ color, dim }: { color: string; dim: string }) => (
  <View className="flex-row items-end gap-[3px]">
    {[14, 20, 26, 32, 22].map((h, i) => (
      <View
        key={i}
        style={{
          width: 3,
          height: h * 0.55,
          borderRadius: 1.5,
          backgroundColor: i < 4 ? color : dim,
        }}
      />
    ))}
  </View>
)

// ─── Sort Bar ─────────────────────────────────────────────────────────────────
function SortBar({ sort, onChange }: { sort: SortMode; onChange: (s: SortMode) => void }) {
  const { t } = useTranslation()
  const colors = useThemeColors()

  return (
    <View className="flex-col gap-2 px-6 py-3 sm:flex-row sm:items-center sm:justify-between">
      <View className="flex-row items-center gap-2">
        <View className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: colors.primary }} />
        <Text
          className="text-xs font-black uppercase tracking-[5px]"
          style={{ color: colors.muted }}
        >
          {t('communityFeed.orderBy')}
        </Text>
      </View>

      <View className="flex-row gap-1.5">
        {(['new', 'top'] as const).map((mode) => {
          const active = sort === mode
          return (
            <Pressable
              key={mode}
              onPress={() => onChange(mode)}
              className="flex-row items-center gap-1.5 px-3 py-1.5"
              style={{
                borderWidth: 1,
                borderColor: active ? colors.primary : `${colors.muted}25`,
                borderRadius: 3,
                backgroundColor: active ? `${colors.primary}12` : 'transparent',
              }}
            >
              {active && (
                <View
                  className="w-1.5 h-1.5 rounded-full"
                  style={{ backgroundColor: colors.primary }}
                />
              )}
              <Text
                className="text-xs font-black uppercase tracking-widest"
                style={{ color: active ? colors.primary : colors.muted }}
              >
                {t(mode === 'new' ? 'communityFeed.sortNew' : 'communityFeed.sortTop')}
              </Text>
            </Pressable>
          )
        })}
      </View>
    </View>
  )
}

function SearchBar({
  value,
  onChangeText,
  onSubmit,
  onClear,
  isActive,
  loading,
}: {
  value: string
  onChangeText: (value: string) => void
  onSubmit: () => void
  onClear: () => void
  isActive: boolean
  loading: boolean
}) {
  const { t } = useTranslation()
  const colors = useThemeColors()

  return (
    <View className="px-6 pb-1">
      <View className="flex-col gap-2 sm:flex-row sm:items-center">
        <View
          className="flex-1 flex-row items-center gap-2 px-3 py-2"
          style={{
            borderWidth: 1,
            borderColor: `${colors.muted}30`,
            borderRadius: 3,
            backgroundColor: colors.surface,
          }}
        >
          <Icon name="search" size={12} color={colors.muted} />
          <TextInput
            value={value}
            onChangeText={onChangeText}
            onSubmitEditing={onSubmit}
            placeholder={t('communityFeed.searchPlaceholder')}
            placeholderTextColor={colors.muted}
            returnKeyType="search"
            maxLength={100}
            className="flex-1 text-sm"
            style={{ color: colors.dark, paddingVertical: 0 }}
          />
        </View>

        <Pressable
          onPress={onSubmit}
          disabled={loading}
          className="px-3 py-2"
          style={{
            borderWidth: 1,
            borderColor: `${colors.primary}45`,
            borderRadius: 3,
            backgroundColor: loading ? `${colors.primary}08` : `${colors.primary}12`,
            opacity: loading ? 0.7 : 1,
          }}
        >
          <Text
            className="text-xs font-black uppercase tracking-[3px]"
            style={{ color: colors.primary }}
          >
            {t('communityFeed.searchButton')}
          </Text>
        </Pressable>

        {isActive && (
          <Pressable
            onPress={onClear}
            disabled={loading}
            className="px-3 py-2"
            style={{
              borderWidth: 1,
              borderColor: `${colors.muted}30`,
              borderRadius: 3,
              backgroundColor: colors.surface,
              opacity: loading ? 0.7 : 1,
            }}
          >
            <Text
              className="text-xs font-black uppercase tracking-[3px]"
              style={{ color: colors.muted }}
            >
              {t('communityFeed.clearSearch')}
            </Text>
          </Pressable>
        )}
      </View>
    </View>
  )
}

// ─── Corner Bracket Decoration ────────────────────────────────────────────────
const CornerBrackets = ({ color }: { color: string }) => (
  <>
    <View
      className="absolute top-0 left-0 w-4 h-4 border-t-2 border-l-2"
      style={{ borderColor: color }}
    />
    <View
      className="absolute top-0 right-0 w-4 h-4 border-t-2 border-r-2"
      style={{ borderColor: color }}
    />
    <View
      className="absolute bottom-0 left-0 w-4 h-4 border-b-2 border-l-2"
      style={{ borderColor: color }}
    />
    <View
      className="absolute bottom-0 right-0 w-4 h-4 border-b-2 border-r-2"
      style={{ borderColor: color }}
    />
  </>
)

// ─── Main Feed ────────────────────────────────────────────────────────────────
// eslint-disable-next-line complexity
export default function Feed({
  composeRef,
  scrollToComposeTick = 0,
}: {
  composeRef: React.RefObject<{ focusAndExpand: () => void } | null>
  scrollToComposeTick?: number
}) {
  const { t } = useTranslation()
  const colors = useThemeColors()
  const { capture } = useAnalytics()
  const { user } = useAuth()
  const {
    posts,
    hasMore,
    loading,
    error,
    searchQuery,
    isSearchActive,
    loadMore,
    submitSearch,
    clearSearch,
    submitPost,
    removePost,
    likePost,
    pinPost,
  } = useCommunityFeed()
  const [sort, setSort] = useState<SortMode>('new')
  const [searchDraft, setSearchDraft] = useState('')
  const [composeVisible, setComposeVisible] = useState(false)

  const accessState = useCommunityFeedAccess(user?.id)
  const flatListRef = useRef<FlatList>(null)

  useEffect(() => {
    if (!user?.id || accessState !== 'allowed') return
    loadMore()
  }, [accessState, loadMore, user?.id])

  useEffect(() => {
    if (scrollToComposeTick <= 0) return
    setComposeVisible(true)
    flatListRef.current?.scrollToOffset({ offset: 0, animated: true })
  }, [scrollToComposeTick])

  useEffect(() => {
    setSearchDraft(searchQuery)
  }, [searchQuery])

  useEffect(() => {
    if (accessState === 'allowed') {
      capture('community_feed_viewed')
      return
    }

    if (accessState === 'blocked') {
      capture('community_feed_access_blocked')
    }
  }, [accessState, capture])

  const sortedPosts = useMemo(() => {
    const unpinned = posts.filter((p) => !p.is_pinned)
    if (sort === 'top') return [...unpinned].sort((a, b) => b.like_count - a.like_count)
    return unpinned
  }, [posts, sort])

  const pinnedPosts = useMemo(() => posts.filter((p) => p.is_pinned), [posts])

  const handleSearchSubmit = () => {
    const trimmed = searchDraft.trim()
    if (!trimmed) {
      void clearSearch()
      return
    }
    void submitSearch(trimmed)
  }

  const handleSubmitPost = async (body: string) => {
    await submitPost(body)
    setComposeVisible(false)
  }

  const handleSortChange = (nextSort: SortMode) => {
    capture('community_feed_sort_changed', { sort_mode: nextSort })
    setSort(nextSort)
  }

  // ── Checking ────────────────────────────────────────────────────────────────
  if (accessState === 'checking') {
    return (
      <View className="flex-1 items-center justify-center gap-5">
        <ActivityIndicator color={colors.primary} size="large" />
        <View className="items-center gap-2">
          <Text
            className="text-xs font-black uppercase tracking-[6px]"
            style={{ color: colors.muted }}
          >
            {t('communityFeed.scanningFrequencies')}
          </Text>
          <View className="flex-row gap-1">
            {Array.from({ length: 8 }).map((_, i) => (
              <View
                key={i}
                className="w-1 h-1 rounded-full"
                style={{ backgroundColor: `${colors.primary}${i % 3 === 0 ? 'ff' : '30'}` }}
              />
            ))}
          </View>
        </View>
      </View>
    )
  }

  // ── Blocked ─────────────────────────────────────────────────────────────────
  if (accessState === 'blocked') {
    return (
      <View className="flex-1 items-center justify-center px-10">
        <View
          className="w-full p-10 items-center gap-4"
          style={{
            borderWidth: 1,
            borderColor: `${colors.muted}20`,
            borderRadius: 2,
          }}
        >
          <CornerBrackets color={colors.primary} />
          <Text
            className="text-xs font-black uppercase tracking-[5px]"
            style={{ color: colors.primary }}
          >
            {t('communityFeed.clearanceRequired')}
          </Text>
          <Icon name="lock" size={28} color={colors.muted} />
          <Text className="text-base font-black text-center" style={{ color: colors.dark }}>
            {t('communityFeed.accessBlockedTitle')}
          </Text>
          <Text className="text-sm text-center leading-relaxed" style={{ color: colors.muted }}>
            {t('communityFeed.accessBlockedBody')}
          </Text>
        </View>
      </View>
    )
  }

  // ── Allowed ──────────────────────────────────────────────────────────────────
  return (
    <View className="flex-1">
      <FlatList
        ref={flatListRef}
        data={sortedPosts}
        keyExtractor={(item) => item.id}
        contentContainerStyle={{ paddingBottom: 40 }}
        ItemSeparatorComponent={FrequencyLine}
        ListHeaderComponent={
          <View>
            {/* + Create Post button */}
            {!composeVisible && (
              <View className="px-0 sm:px-6 pt-4 pb-2">
                <Pressable
                  testID="community-compose-create"
                  onPress={() => {
                    setComposeVisible(true)
                    // Scroll to top so compose box is visible
                    flatListRef.current?.scrollToOffset({ offset: 0, animated: true })
                  }}
                  className="flex-row items-center justify-center gap-2 py-4"
                  style={{
                    borderWidth: 1.5,
                    borderColor: colors.primary,
                    borderRadius: 4,
                    backgroundColor: `${colors.primary}18`,
                  }}
                >
                  <Icon name="plus" size={16} color={colors.primary} />
                  <Text
                    className="text-xs font-black uppercase tracking-[5px]"
                    style={{ color: colors.primary }}
                  >
                    {t('communityFeed.createPost')}
                  </Text>
                </Pressable>
              </View>
            )}

            {/* Compose / Broadcast Box — hidden until toggled */}
            {composeVisible && (
              <View className="px-0 sm:px-6 pt-4 pb-2">
                <View
                  style={{
                    borderWidth: 1,
                    borderColor: `${colors.primary}28`,
                    borderRadius: 3,
                    backgroundColor: `${colors.primary}06`,
                  }}
                >
                  <View
                    className="flex-row items-center justify-between px-4 py-2"
                    style={{
                      borderBottomWidth: 1,
                      borderColor: `${colors.primary}18`,
                    }}
                  >
                    <View className="flex-row items-center gap-2">
                      <View
                        className="w-1.5 h-1.5 rounded-full"
                        style={{ backgroundColor: colors.primary }}
                      />
                      <Text
                        className="text-xs font-black uppercase tracking-[5px]"
                        style={{ color: colors.primary }}
                      >
                        {t('communityFeed.newPost')}
                      </Text>
                    </View>
                    <Pressable onPress={() => setComposeVisible(false)} hitSlop={8}>
                      <Icon name="times" size={16} color={colors.muted} />
                    </Pressable>
                  </View>
                  <ComposeBox ref={composeRef} onSubmit={handleSubmitPost} loading={loading} />
                </View>
              </View>
            )}

            {/* Pinned Posts Section */}
            {pinnedPosts.length > 0 && (
              <View className="px-0 sm:px-6 pt-4 pb-2">
                <View
                  style={{
                    borderWidth: 1.5,
                    borderColor: `${colors.primary}45`,
                    borderRadius: 4,
                    backgroundColor: `${colors.primary}08`,
                    overflow: 'hidden',
                  }}
                >
                  <CornerBrackets color={colors.primary} />

                  {/* Pinned header bar */}
                  <View
                    className="flex-row items-center gap-2.5 px-4 py-3"
                    style={{
                      borderBottomWidth: 1,
                      borderColor: `${colors.primary}20`,
                      backgroundColor: `${colors.primary}10`,
                    }}
                  >
                    <View
                      className="w-6 h-6 rounded items-center justify-center"
                      style={{ backgroundColor: `${colors.primary}20` }}
                    >
                      <Icon name="thumb-tack" size={12} color={colors.primary} />
                    </View>
                    <Text
                      className="text-xs font-black uppercase tracking-[5px]"
                      style={{ color: colors.primary }}
                    >
                      {t('communityFeed.pinned')}
                    </Text>
                    <View
                      className="ml-auto px-2 py-0.5 rounded"
                      style={{ backgroundColor: `${colors.primary}15` }}
                    >
                      <Text className="text-[10px] font-black" style={{ color: colors.primary }}>
                        {pinnedPosts.length}
                      </Text>
                    </View>
                  </View>

                  {/* Pinned posts */}
                  <View className="px-3 py-3 gap-3">
                    {pinnedPosts.map((post) => (
                      <PostCard
                        key={post.id}
                        post={post}
                        currentUserId={user?.id ?? ''}
                        onDelete={removePost}
                        onLike={likePost}
                        onPin={pinPost}
                      />
                    ))}
                  </View>
                </View>
              </View>
            )}

            <View className="px-0 sm:px-6 pt-4 pb-2" />
            <FrequencyLine />
            <View className="px-0 sm:px-6 pt-4 pb-2" />

            <SearchBar
              value={searchDraft}
              onChangeText={setSearchDraft}
              onSubmit={handleSearchSubmit}
              onClear={() => {
                setSearchDraft('')
                void clearSearch()
              }}
              isActive={isSearchActive}
              loading={loading}
            />

            <SortBar sort={sort} onChange={handleSortChange} />
            <FrequencyLine />
          </View>
        }
        renderItem={({ item }) => (
          <View className="px-0 sm:px-6 py-3">
            <PostCard
              post={item}
              currentUserId={user?.id ?? ''}
              onDelete={removePost}
              onLike={likePost}
              onPin={pinPost}
            />
          </View>
        )}
        ListEmptyComponent={
          !loading ? (
            <View className="items-center py-20 gap-3">
              <View className="flex-row items-end gap-[3px]">
                {[1, 2, 1, 2, 1].map((h, i) => (
                  <View
                    key={i}
                    style={{
                      width: 4,
                      height: h * 8,
                      borderRadius: 2,
                      backgroundColor: `${colors.border}60`,
                    }}
                  />
                ))}
              </View>
              <Text
                className="text-xs font-black uppercase tracking-[5px]"
                style={{ color: colors.muted }}
              >
                {isSearchActive ? t('communityFeed.noSearchResults') : t('communityFeed.noPosts')}
              </Text>
            </View>
          ) : null
        }
        ListFooterComponent={
          loading ? (
            <View className="items-center py-6 gap-2">
              <ActivityIndicator color={colors.primary} size="small" />
              <Text
                className="text-xs font-black uppercase tracking-[4px]"
                style={{ color: colors.muted }}
              >
                {t('communityFeed.loadingSignal')}
              </Text>
            </View>
          ) : hasMore ? (
            <Pressable
              className="mx-6 my-4 py-4 items-center"
              style={{
                borderWidth: 1,
                borderColor: `${colors.primary}35`,
                borderRadius: 3,
              }}
              onPress={loadMore}
            >
              <View className="flex-row items-center gap-2">
                <View
                  className="w-1 h-1 rounded-full"
                  style={{ backgroundColor: colors.primary }}
                />
                <Text
                  className="text-xs font-black uppercase tracking-[5px]"
                  style={{ color: colors.primary }}
                >
                  {t('communityFeed.loadMore')}
                </Text>
                <View
                  className="w-1 h-1 rounded-full"
                  style={{ backgroundColor: colors.primary }}
                />
              </View>
            </Pressable>
          ) : sortedPosts.length > 0 ? (
            <View className="flex-row items-center px-6 my-4 gap-3">
              <View className="h-px flex-1 opacity-10" style={{ backgroundColor: colors.muted }} />
              <Text
                className="text-[9px] font-black uppercase tracking-[5px]"
                style={{ color: `${colors.muted}60` }}
              >
                {t('communityFeed.endOfFeed')}
              </Text>
              <View className="h-px flex-1 opacity-10" style={{ backgroundColor: colors.muted }} />
            </View>
          ) : null
        }
        onEndReached={() => {
          if (hasMore && !loading) loadMore()
        }}
        onEndReachedThreshold={0.5}
      />

      {/* Error toast */}
      {error === 'post' && (
        <View
          className="absolute bottom-10 left-6 right-6 px-5 py-4 flex-row items-center gap-3"
          style={{
            backgroundColor: '#150000',
            borderWidth: 1,
            borderColor: '#ef444435',
            borderRadius: 3,
          }}
        >
          <View className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: '#ef4444' }} />
          <Text className="text-sm font-medium flex-1" style={{ color: '#ef4444' }}>
            {t('communityFeed.postError')}
          </Text>
        </View>
      )}
    </View>
  )
}

// ─── Access Hook ──────────────────────────────────────────────────────────────
function useCommunityFeedAccess(userId: string | undefined): FeedAccessState {
  const [accessState, setAccessState] = useState<FeedAccessState>('checking')

  useEffect(() => {
    let cancelled = false
    if (!userId) {
      setAccessState('checking')
      return
    }
    setAccessState('checking')
    getMyCohort()
      .then((cohort) => {
        if (cancelled) return
        setAccessState(cohort.member_status === 'selected' ? 'allowed' : 'blocked')
      })
      .catch((err: unknown) => {
        if (cancelled) return
        const status = (err as { status?: number }).status
        setAccessState(status === 403 || status === 404 ? 'blocked' : 'allowed')
      })
    return () => {
      cancelled = true
    }
  }, [userId])

  return accessState
}
